import { consola } from 'consola'
import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { saveBranchChoice, clearBranchChoice, loadBranchChoice, restorePersistedBranchId, SESSION_BRANCH_KEY } from '../lib/branchChoice';
import type {
  Institution,
  Branch,
  CityInfo,
} from '@entities/institution';
import type { ProductsCategoryV2, APIProductV2 } from '~/utils/types/Product';
import type { ProductTag } from '~/utils/types/ProductTag';
import apiInstitutions from '~/entities/institution/api';
import { apiProductInstance, apiFavorite , APIProductTags, buildMenuNodeIndex, resolveBranchAliases } from '~/entities/product/@x/institution';
import { useUserProfileStore } from '~/entities/user/@x/institution';
import { debounce } from '@shared/lib';



interface Addon {
  id: number;
  name: string;
  description?: string;
  price: number;
  img?: {
    key?: string;
    url?: string;
  };
  required?: boolean;
  max_quantity?: number;
}

// Для обратной совместимости - старые интерфейсы (deprecated)
/** @deprecated Use APIProductV2 instead */
interface Product {
  id: number;
  name: string;
  description: string;
  price: string;
  img: string;
  categoryId: number;
  is_favorite?: boolean;
}

/** @deprecated Use ProductsCategoryV2 instead */
type _ProductsCategory = {
  category: string;
  description: string;
  to: string;
  img: string;
  products: Product[];
}

/** @deprecated Use ProductsCategoryV2 instead */
type _FavoritesCategory = {
  category: string;
  description: string;
  to: string;
  img: string;
  products: Product[];
}

/**
 * Хранилище для управления данными учреждений, включая филиалы, товары и избранное
 */
export const useInstitutionStore = defineStore('institution', () => {

  const storeUserProfile = useUserProfileStore();

  const institutionId = ref<number>(
    process.client ? Number(sessionStorage.getItem('institutionId') || 1) : 1,
  )

  const setInstitutionId = (newId: number) => {
    institutionId.value = newId
    if (process.client) sessionStorage.setItem('institutionId', String(newId))
  }

  // Состояние
  /** Текущее выбранное учреждение */
  const institution = ref<Institution | null>(null);

  /** Список всех филиалов учреждения */
  const branches = ref<Branch[]>([]);

  /** Товары, сгруппированные по категориям (API v2) */
  const products = ref<ProductsCategoryV2[]>([]);

  /** Филиал, которому принадлежит текущее меню (заполняется в fetchProducts) */
  const productsBranchId = ref<number | null>(null);

  /** Доптовары */
  const addons = ref<Addon[]>([]);

  /** Отбавки (исключаемые ингредиенты) */
  const excludeIngredients = ref<any[]>([]);

  /** Теги продуктов */
  const productTags = ref<ProductTag[]>([]);

  /** Выбранные теги для фильтрации (массив ID) */
  const selectedTagIds = ref<number[]>([]);

  // В самом хранилище (после других состояний)
  const favorites = ref<ProductsCategoryV2[]>([]);

  /** Поисковый запрос для товаров */
  const searchQuery = ref('');

  /** Дебаунсированная версия поискового запроса */
  const debouncedSearchQuery = ref('');

  /**
   * ID текущего выбранного филиала — ЕДИНЫЙ источник истины выбора филиала.
   * Раньше выбор хранился по названию города (selectedCity), из-за чего два
   * филиала в одном городе были неразличимы и выбор «залипал» на первом.
   * Теперь город/адрес/телефон производны от выбранного по id филиала.
   */
  const selectedBranchId = ref<number | null>(
    // Филиалы ещё не загружены — восстанавливаем без проверки активности,
    // валидация с чисткой протухшего выбора идёт в init() лейаута
    process.client ? restorePersistedBranchId() : null,
  );

  const setSelectedBranchId = (id: number | null) => {
    selectedBranchId.value = id;
    if (process.client) {
      if (id == null) sessionStorage.removeItem(SESSION_BRANCH_KEY);
      else sessionStorage.setItem(SESSION_BRANCH_KEY, String(id));
    }
  };

  /**
   * ЯВНЫЙ выбор филиала пользователем (шапка, футер, мобильное меню, модалка с картой).
   *
   * В отличие от setSelectedBranchId (любая смена, включая авто-роутинг доставки),
   * выбор сохраняется в localStorage на сутки (branchChoice) и переживает обновление
   * страницы: авто-логика при старте его не перетирает. Через сутки выбор протухает,
   * и стартовая шторка (features/branch-select-prompt) спросит локацию заново.
   */
  const chooseBranch = (id: number | null) => {
    if (id == null) clearBranchChoice();
    else saveBranchChoice(id);
    setSelectedBranchId(id);
  };

  // Геттеры

  /**
   * Список городов с информацией о филиалах
   * @returns {CityInfo[]} Массив объектов с информацией о городах
   */
  const cities = computed<CityInfo[]>(() =>
    branches.value
      .filter(branch => branch.is_active !== false)
      .map(branch => ({
        id: branch.id,
        name: branch.address.city,
        phone: branch.phone,
        address: branch.address.street,
        working_hours: branch.working_hours || { hours: '10:00-22:00' },
      })),
  )

  /**
   * Текущий выбранный филиал на основе selectedBranchId
   * @returns {Branch | null} Выбранный филиал или null если не найден
   */
  const selectedBranch = computed(() =>
    branches.value.find(branch => branch.id === selectedBranchId.value) || null,
  );

  /**
   * Опции выбора филиала для UI: по одному пункту на филиал, с понятной
   * подписью «Город, Улица», чтобы различать филиалы в одном городе.
   * value пунктов — id филиала (не строка-город).
   */
  const branchOptions = computed(() =>
    branches.value
      .filter(branch => branch.is_active !== false)
      .map(branch => ({
        id: branch.id,
        city: branch.address.city,
        street: branch.address.street,
        // Сначала улица + дом, затем станица/город — так короче и информативнее
        label: [branch.address.street, branch.address.city].filter(Boolean).join(', '),
      })),
  );

  /**
   * Название выбранного города — производное от выбранного филиала.
   * Сеттер сохранён для обратной совместимости со старым кодом, который писал
   * город напрямую: выбираем первый активный филиал в этом городе.
   * Новый код должен использовать setSelectedBranchId(id).
   */
  const selectedCity = computed<string>({
    get: () => selectedBranch.value?.address.city ?? '',
    set: (cityName: string) => {
      const branch = branches.value.find(
        b => b.address.city === cityName && b.is_active !== false,
      );
      if (branch) setSelectedBranchId(branch.id);
    },
  });

  /**
   * Информация о текущем выбранном городе/филиале
   * @returns {CityInfo} Объект с информацией о выбранном филиале
   */
  const currentCity = computed(() =>
    cities.value.find(city => city.id === selectedBranchId.value) || cities.value[0], // Первый филиал по умолчанию
  );

  /**
   * Есть ли активные фильтры по тегам
   */
  const hasActiveTagFilters = computed(() => selectedTagIds.value.length > 0);

  /**
   * Общее количество товаров после фильтрации
   */
  const totalProductsCount = computed(() => {
    if (!products.value || !Array.isArray(products.value)) {
      return 0;
    }
    return products.value.reduce((sum, cat) => {
      if (!cat || !cat.products || !Array.isArray(cat.products)) {
        return sum;
      }
      return sum + cat.products.length;
    }, 0);
  });

  /**
   * Есть ли результаты после фильтрации
   */
  const hasFilteredResults = computed(() => totalProductsCount.value > 0);

  // Вотчеры
  /**
   * Инициализирует все необходимые вотчеры для хранилища
   * @private
   */
  const setupWatchers = () => {
    // Выбор филиала по умолчанию при загрузке списка филиалов.
    // Если сохранённый (sessionStorage) филиал ещё не загружен — оставляем как есть.
    // Если он отсутствует/неактивен в новом списке — берём первый активный.
    watch(branches, (newBranches) => {
      if (newBranches.length === 0) return;
      const exists = newBranches.some(
        b => b.id === selectedBranchId.value && b.is_active !== false,
      );
      if (!exists) {
        // Выключенный посреди сессии филиал мог быть и СОХРАНЁННЫМ выбором:
        // чистим запись, иначе hasFreshBranchChoice() до конца суток глушил бы
        // шторку и авто-роутинг на филиале, который человек фактически не выбирал
        if (selectedBranchId.value != null && loadBranchChoice() === selectedBranchId.value) {
          clearBranchChoice();
        }
        const firstActive = newBranches.find(b => b.is_active !== false) ?? newBranches[0];
        setSelectedBranchId(firstActive?.id ?? null);
      }
    }, { immediate: true });

    // Загрузка товаров при изменении выбранного филиала
    watch(selectedBranchId, (newId) => {
      if (newId) {
        fetchProducts(newId, storeUserProfile.getUserId);
      }
    });

    // ✅ Сохранение номера телефона при изменении города
    watch(currentCity, async (newCity) => {
      if (newCity?.phone) {
        try {
          const { useInstitutionPhoneStorage } = await import('./useInstitutionPhoneStorage')
          const phoneStorage = useInstitutionPhoneStorage()
          await phoneStorage.setInstitutionPhone(newCity.phone)
          consola.debug('✅ Номер телефона обновлен при смене города:', newCity.phone)
        } catch (error) {
          consola.warn('⚠️ Не удалось обновить номер телефона при смене города:', error)
        }
      }
    });

    watch(() => storeUserProfile.getUserId, (idNew) => {
      // ✅ Проверяем, что есть и userId и branchId
      if (idNew && selectedBranch.value?.id) {
        fetchProducts(selectedBranch.value.id, idNew);
      }
    });

    // Дебаунс поискового запроса: реальный запрос на бэк уходит не чаще раза
    // в 500мс после остановки ввода и только если итоговый текст изменился (дедуп),
    // чтобы не слать пачку запросов на каждое нажатие клавиши.
    const runSearch = debounce((rawQuery: string) => {
      const normalized = rawQuery.trim().toLowerCase();

      // Дедуп: тот же запрос (с учётом регистра и пробелов) повторно не отправляем
      if (normalized === debouncedSearchQuery.value) return;
      debouncedSearchQuery.value = normalized;

      // ✅ Проверяем, что selectedBranch и id существуют
      if (selectedBranch.value && selectedBranch.value.id) {
        fetchProducts(selectedBranch.value.id, storeUserProfile.getUserId);
      }
    }, 500);

    watch(searchQuery, newQuery => runSearch(newQuery));

    // Перезагрузка товаров при изменении выбранных тегов
    watch(selectedTagIds, (newTagIds, oldTagIds) => {
      const oldArray = Array.isArray(oldTagIds) ? [...oldTagIds] : [];
      const newArray = Array.isArray(newTagIds) ? [...newTagIds] : [];

      consola.debug('[watch] 🏷️ Фильтр тегов изменен:', {
        old: oldArray,
        new: newArray,
        oldLength: oldArray.length,
        newLength: newArray.length,
        branchId: selectedBranch.value?.id,
        userId: storeUserProfile.getUserId,
      });

      // ✅ Проверяем, что selectedBranch и id существуют
      if (selectedBranch.value && selectedBranch.value.id) {
        consola.debug('[watch] 🔄 Перезагружаем товары с новыми фильтрами...');
        fetchProducts(selectedBranch.value.id, storeUserProfile.getUserId);
      } else {
        consola.warn('[watch] ⚠️ Пропускаем загрузку: нет selectedBranch');
      }
    }, { deep: true });
  };

  // Действия

  /**
   * Преобразует филиал из нового формата API (BranchDto) в старый формат (Branch)
   * @private
   */
  const transformBranchFromDto = (branchDto: any): Branch => {
    // Парсим адрес - если это строка, разбиваем на city и street
    let addressObj: { city: string; street: string };

    if (typeof branchDto.address === 'string') {
      // Пытаемся разделить адрес на город и улицу
      // Формат может быть: "Город, Улица" или просто "Адрес"
      const parts = branchDto.address.split(',').map((s: string) => s.trim());
      addressObj = {
        city: parts.length > 1 ? parts[0] : 'Не указан',
        street: parts.length > 1 ? parts.slice(1).join(', ') : branchDto.address,
      };
    } else if (typeof branchDto.address === 'object') {
      // Если уже объект - используем как есть
      addressObj = branchDto.address;
    } else {
      // Fallback
      addressObj = { city: 'Не указан', street: branchDto.address || 'Не указан' };
    }

    return {
      id: typeof branchDto.id === 'string' ? Number.parseInt(branchDto.id) : branchDto.id,
      name: branchDto.name,
      address: addressObj,
      phone: branchDto.phone || '',
      working_hours: branchDto.working_hours || { hours: '10:00-22:00' },
      is_active: branchDto.is_active !== false,
      latitude: branchDto.latitude,
      longitude: branchDto.longitude,
      tables:
        branchDto.tables == null
          ? []
          : branchDto.tables.map((table: any) => ({
              id: typeof table.id === 'string' ? Number.parseInt(table.id) : table.id,
              number: table.table_number,
              branch_id: typeof branchDto.id === 'string' ? Number.parseInt(branchDto.id) : branchDto.id,
            })),

      // Workload Control Board — пробрасываем из DTO, если бэк их вернул
      accepting_orders: branchDto.accepting_orders,
      delivery_stopped: branchDto.delivery_stopped,
      effective_kitchen_time_min: branchDto.effective_kitchen_time_min,
      effective_kitchen_time_max: branchDto.effective_kitchen_time_max,
      effective_courier_time_min: branchDto.effective_courier_time_min,
      effective_courier_time_max: branchDto.effective_courier_time_max,
    };
  };

  /**
   * Загружает филиалы для указанного учреждения
   * Новый API: GET /institutions/{id}
   *
   * @param {number} institutionId - ID учреждения
   * @returns {Promise<void>}
   * @throws {Error} При ошибке загрузки филиалов
   */
  const fetchBranches = async (institutionId: number) => {
    // Проверяем корректность ID перед запросом
    if (!institutionId || Number.isNaN(institutionId) || institutionId <= 0) {
      const errorMsg = `Некорректный ID филиала: ${institutionId}. Ожидается положительное число.`;
      throw new Error(errorMsg);
    }

    try {
      const { data } = await apiInstitutions.getInstitution(institutionId);

      if (!data || !data.institution) {
        throw new Error(`Данные учреждения не найдены для ID: ${institutionId}`);
      }

      // Преобразуем InstitutionFullDto в Institution
      const institutionData = data.institution;

      // Для обратной совместимости: создаем delivery_rules из delivery_zones
      // Берем правила из первой зоны (если она есть) для старого кода
      const legacyDeliveryRules = institutionData.delivery_zones?.[0]?.rules?.map(rule => ({
        min_order_amount: rule.price_range_from,
        max_order_amount: rule.price_range_to ?? null,
        delivery_cost: rule.delivery_cost,
      })) || [];

      // Преобразуем delivery_zones для совместимости со старым кодом
      // Маппим поля из нового API в старый формат
      const transformedDeliveryZones =
        institutionData.delivery_zones == null
          ? []
          : institutionData.delivery_zones.map(zone => ({
              id: zone.id,
              zone: zone.zone,
              zone_coords: zone.zone_coords,
              center_zone_coords: zone.center_zone_coords,
              min_order_limit: zone.zone_min_amount, // Маппинг: zone_min_amount -> min_order_limit
              rules:
                zone.rules == null
                  ? []
                  : zone.rules.map(rule => ({
                      id: rule.id,
                      min_order_amount: rule.price_range_from, // Маппинг: price_range_from -> min_order_amount
                      max_order_amount: rule.price_range_to ?? null, // Маппинг: price_range_to -> max_order_amount
                      delivery_cost: rule.delivery_cost,
                      from_time: rule.from_time || '00:00',
                      to_time: rule.to_time || '23:59',
                    })),
            }));

      institution.value = {
        id: institutionData.id,
        name: institutionData.name,
        director_manager: institutionData.director_manager,
        telegram_name: institutionData.telegram_name,
        yandex_auth_enabled: institutionData.yandex_auth_enabled,
        telegram_auth_enabled: institutionData.telegram_auth_enabled,
        max_auth_enabled: institutionData.max_auth_enabled,
        vk_auth_enabled: (institutionData as any).vk_auth_enabled,
        vk_app_id: (institutionData as any).vk_app_id,
        vk_redirect_url: (institutionData as any).vk_redirect_url,
        vk_app_id_android: (institutionData as any).vk_app_id_android,
        vk_redirect_url_android: (institutionData as any).vk_redirect_url_android,
        vk_app_id_ios: (institutionData as any).vk_app_id_ios,
        vk_redirect_url_ios: (institutionData as any).vk_redirect_url_ios,
        telegram_verification_required: institutionData.telegram_verification_required,
        service_fee_percent: institutionData.service_fee_percent,
        social_networks: institutionData.social_networks as any,
        legal_information: institutionData.legal_information as any,
        active_payment_system: institutionData.active_payment_system as any,

        // Новые поля из API v2 (с трансформацией для совместимости)
        delivery_zones: transformedDeliveryZones as any,
        loyalty_enabled: institutionData.loyalty_enabled,
        payment_paused: institutionData.payment_paused,
        marketing_consent_required: institutionData.marketing_consent_required,
        loyalty_max_percent_redeem: institutionData.loyalty_max_percent_redeem,
        loyalty_levels:
          institutionData.loyalty_levels == null
            ? []
            : institutionData.loyalty_levels.map(level => ({
                percent: level.percent.toString(),
                min_spent: level.min_spent.toString(),
              })),
        loyalty_welcome_mode: institutionData.loyalty_welcome_mode,
        loyalty_welcome_bonus_points: institutionData.loyalty_welcome_bonus_points,
        loyalty_first_orders_count: institutionData.loyalty_first_orders_count,
        loyalty_first_order_steps: (institutionData as any).loyalty_first_order_steps,
        loyalty_first_orders_min_order_amount: (institutionData as any).loyalty_first_orders_min_order_amount,
        loyalty_first_orders_max_discount: (institutionData as any).loyalty_first_orders_max_discount,
        // Ограничения СПИСАНИЯ баллов: корзина прячет форму списания до того,
        // как придёт preview-расчёт (например, доставка без указанного адреса).
        loyalty_redeem_min_order_amount: (institutionData as any).loyalty_redeem_min_order_amount,
        loyalty_redeem_from_order_number: (institutionData as any).loyalty_redeem_from_order_number,
        alfabank_enabled: institutionData.alfabank_enabled,
        tbusiness_terminal_key: institutionData.tbusiness_terminal_key,
        seo_meta: institutionData.seo_meta,
        visual: institutionData.visual,
        legal_information_scheme: institutionData.legal_information_scheme,
        legal_information_phone: institutionData.legal_information_phone,
        legal_information_email: institutionData.legal_information_email,
        frontend_url: institutionData.frontend_url,
        google_play_url: institutionData.google_play_url,
        app_store_url: institutionData.app_store_url,
        web_app_promo_phone_image_url: institutionData.web_app_promo_phone_image_url,
        web_app_install_qr_image_url: institutionData.web_app_install_qr_image_url,

        // Для обратной совместимости со старым кодом
        delivery_rules: legacyDeliveryRules,
      } as any;

      consola.debug('🏢 Institution loaded with SEO data:', {
        institutionId: institutionData.id,
        name: institutionData.name,
        hasSeoMeta: !!institutionData.seo_meta,
        seoMeta: institutionData.seo_meta,
        googlePlayUrl: institutionData.google_play_url,
        appStoreUrl: institutionData.app_store_url,
      });

      // Преобразуем филиалы из нового формата в старый
      branches.value = data.branches.map(transformBranchFromDto);

      // ✅ Сохраняем номер телефона заведения для офлайн режима
      // Используем первый активный филиал или первый доступный
      try {
        const activeBranch =
          branches.value.find(b => b.is_active !== false) ?? branches.value[0]
        const phoneToSave = activeBranch?.phone
        if (phoneToSave) {
          const { useInstitutionPhoneStorage } = await import('./useInstitutionPhoneStorage')
          const phoneStorage = useInstitutionPhoneStorage()
          await phoneStorage.setInstitutionPhone(phoneToSave)
          consola.debug('✅ Номер телефона заведения сохранен для офлайн режима:', phoneToSave)
        }
      } catch (phoneError) {
        consola.warn('⚠️ Не удалось сохранить номер телефона для офлайн режима:', phoneError)
        // Не критично, продолжаем работу
      }

      // Загружаем теги продуктов для учреждения
      await fetchProductTags(institutionId);

    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Неизвестная ошибка при загрузке филиалов';

      // Более информативная ошибка для пользователя
      if (error.response?.status === 404) {
        throw new Error(`Учреждение с ID ${institutionId} не найдено`);
      } else if (error.response?.status >= 500) {
        throw new Error('Сервер недоступен. Попробуйте позже.');
      } else {
        throw new Error(`Ошибка при получении данных о заведении: ${errorMessage}`);
      }
    }
  };

  /**
   * Загружает товары для указанного филиала с возможностью поиска
   * @param {number} branchId - ID филиала
   * @param {number} userId - ID текущего пользователя (опционально)
   * @returns {Promise<void>}
   * @throws {Error} При ошибке загрузки или неверном формате данных
   */
  const fetchProducts = async (branchId: number, userId?: number) => {
    // ✅ ВАЖНО: Не отправляем запрос, если branchId невалиден
    if (!branchId || Number.isNaN(branchId)) {
      consola.warn('[fetchProducts] ⚠️ Пропускаем загрузку: невалидный branchId:', branchId);
      return;
    }

    try {
      const tagsArray = Array.isArray(selectedTagIds.value) ? [...selectedTagIds.value] : [];
      consola.debug('[fetchProducts] 📦 Загружаем продукты:', {
        branchId,
        userId,
        isAuth: !!userId,
        selectedTags: tagsArray,
        selectedTagsLength: tagsArray.length,
        willFilterByTags: tagsArray.length > 0,
      });

      // Получаем сразу и категории с товарами, и полный список доптоваров
      // Передаем выбранные теги для фильтрации (только если есть теги)
      const tagsToSend = tagsArray.length > 0 ? tagsArray : undefined;
      const { categories, allAddons, allExcludeIngredients } = await apiProductInstance.getProducts(
        branchId,
        debouncedSearchQuery.value,
        userId,
        tagsToSend,
      );

      // Если пришёл одиночный объект вместо массива — оборачиваем (до логов и normalize)
      const categoriesList = Array.isArray(categories) ? categories : [categories];

      consola.debug('[fetchProducts] 📥 RAW данные с бэка:', {
        categoriesCount: categoriesList.length,
        categories: categoriesList.map(cat => ({
          name: cat.category,
          productsCount: cat.products?.length || 0,
          products: cat.products,
        })),
      });

      // Сохраняем полный список допов
      addons.value = allAddons;

      // Сохраняем отбавки (exclude_ingredients)
      excludeIngredients.value = allExcludeIngredients || [];

      // Порядок категорий уже отсортирован на бэке
      products.value = normalizeProducts(categoriesList);
      // Меню принадлежит филиалу: у каждой точки свои карточки, свои цены и свой набор
      // комбо. Потребители, которые строятся ПОВЕРХ меню («вы заказывали», повтор заказа),
      // обязаны знать, для какой точки оно приехало, — иначе после смены филиала они
      // соберутся по меню прошлой и предложат то, чего здесь нет.
      productsBranchId.value = branchId;

      consola.debug('[fetchProducts] ✅ Продукты загружены в store:', {
        categoriesCount: products.value.length,
        categories: products.value.map(cat => ({
          name: cat.category,
          productsCount: cat.products?.length || 0,
        })),
        totalProducts: products.value.reduce((sum, cat) => sum + cat.products.length, 0),
        favoritesCount: products.value.reduce((sum, cat) =>
          sum + cat.products.filter(p => p.is_favorite).length, 0,
        ),
        filteredByTags: selectedTagIds.value.length > 0,
      });
    } catch (error) {
      consola.error('Ошибка загрузки товаров:', error);
      throw error;
    }
  };

  /**
   * Приводит избранное к карточкам ТЕКУЩЕГО филиала.
   *
   * Избранное хранится по заведению и по карточке, а карточка принадлежит филиалу:
   * блюдо, добавленное в избранное на другой точке, здесь имеет другой id — раньше
   * оно показывалось карточкой чужого филиала (с её ценой), а иногда и вовсе снятой
   * с продажи. Сервер приводит id к карточкам этого филиала через группы «одно
   * блюдо», после чего мы подменяем позиции узлами меню — так цена, стоп-лист и
   * бейджи совпадают с каталогом.
   *
   * Меню ещё не загружено — возвращаем как есть: избранное лучше показать «как
   * было», чем не показать вовсе.
   */
  const resolveFavoritesToBranch = async (
    categoriesList: ProductsCategoryV2[],
  ): Promise<ProductsCategoryV2[]> => {
    const branchId = selectedBranchId.value;
    if (!branchId || !products.value?.length) return categoriesList;

    // Общий индекс меню и резолв — из entities/product (сбой резолва не роняет)
    const { byProductId } = buildMenuNodeIndex(products.value);

    const foreignIds = categoriesList
      .flatMap(category => (category?.products || []) as any[])
      .map(product => String(product?.id ?? ''))
      .filter(id => id && !byProductId.has(id));

    const resolved = await resolveBranchAliases(foreignIds, branchId);
    const aliasNodes = new Map(
      resolved
        .filter(entry => entry.product_id && byProductId.has(entry.product_id))
        .map(entry => [entry.requested, byProductId.get(entry.product_id!)]),
    );

    // Собираем категории заново из узлов меню; дубли (одно блюдо в избранном
    // двумя карточками разных филиалов) схлопываются по id узла.
    const seen = new Set<string>();
    return categoriesList
      .map((category) => {
        const items = ((category?.products || []) as any[])
          .map((product) => {
            const id = String(product?.id ?? '');
            return byProductId.get(id) ?? aliasNodes.get(id) ?? null;
          })
          .filter((node) => {
            if (!node) return false;
            const nodeId = String(node.id);
            if (seen.has(nodeId)) return false;
            seen.add(nodeId);
            return true;
          });

        return { ...category, products: items };
      })
      .filter(category => category.products.length > 0);
  };

  const fetchFavorites = async (institutionID: number) => {
    try {
      consola.debug('[fetchFavorites] ⭐ Загружаем избранное:', {
        institutionID,
      });

      const favoritesData = await apiFavorite.getFavorites(institutionID);

      // favoritesData.categories — всегда массив или undefined
      const categoriesList = Array.isArray(favoritesData.categories) ? favoritesData.categories : [];

      favorites.value = await resolveFavoritesToBranch(normalizeProducts(categoriesList));

      consola.debug('[fetchFavorites] ✅ Избранное загружено:', {
        categoriesCount: favorites.value.length,
        totalFavorites: favorites.value.reduce((sum, cat) => sum + cat.products.length, 0),
      });
    } catch (error) {
      consola.error('Ошибка загрузки избранных товаров:', error);
      throw error;
    }
  };

  /**
   * Загружает теги продуктов для указанного учреждения
   * API: GET /product-tags/{institutionId}
   *
   * @param {number} institutionID - ID учреждения
   * @returns {Promise<void>}
   * @throws {Error} При ошибке загрузки тегов
   */
  const fetchProductTags = async (institutionID: number) => {
    try {
      consola.debug('[fetchProductTags] 🏷️ Загружаем теги для учреждения:', institutionID);
      const tags = await APIProductTags.getActiveTagsByInstitution(institutionID);
      productTags.value = tags;
      consola.debug('[fetchProductTags] ✅ Теги загружены:', tags.length);
    } catch (error) {
      consola.error('Ошибка загрузки тегов продуктов:', error);
      // Не прерываем работу приложения при ошибке загрузки тегов
      productTags.value = [];
    }
  };

  // Добавляем геттер для плоского списка избранных товаров
  const favoriteProducts = computed<APIProductV2[]>(() => {
    return favorites.value.flatMap(category => category.products);
  })

  /**
   * Получить тег по ID
   * @param {number} tagId - ID тега
   * @returns {ProductTag | undefined} Тег или undefined
   */
  const getTagById = (tagId: number): ProductTag | undefined => {
    return productTags.value.find(tag => tag.id === tagId);
  };

  /**
   * Получить массив тегов по массиву ID
   * @param {number[]} tagIds - Массив ID тегов
   * @returns {ProductTag[]} Массив тегов
   */
  const getTagsByIds = (tagIds: number[]): ProductTag[] => {
    if (!Array.isArray(tagIds) || tagIds.length === 0) return [];
    return tagIds
      .map(id => productTags.value.find(tag => tag.id === id))
      .filter((tag): tag is ProductTag => tag !== undefined);
  };

  /**
   * Нормализует данные товаров, округляя цены
   * @param {ProductsCategoryV2[]} categories - Массив категорий товаров
   * @returns {ProductsCategoryV2[]} Нормализованные данные товаров
   * @private
   */
  const normalizeProducts = (categories: ProductsCategoryV2[]): ProductsCategoryV2[] => {
    return categories.map(category => {
      return {
        ...category,
        products: Array.isArray(category.products)
          ? category.products.map(product => {
              if (product.type === 'group') {
                // Для группового продукта нормализуем цены вариантов
                const normalizedGroup = {
                  ...product,
                  variants: product.variants.map(variant => ({
                    ...variant,
                    price: Math.round(Number(variant.price) || 0),
                  })),
                };
                // Проверяем сохранность is_favorite для групп
                if (product.is_favorite !== normalizedGroup.is_favorite) {
                  consola.warn('[normalizeProducts] ⚠️ is_favorite потерялся для группы:', product.id);
                }
                return normalizedGroup;
              } else {
                // Для одиночного продукта нормализуем цену напрямую
                const normalizedSingle = {
                  ...product,
                  price: Math.round(Number(product.price) || 0),
                };
                // Проверяем сохранность is_favorite для одиночных товаров
                if (product.is_favorite !== normalizedSingle.is_favorite) {
                  consola.warn('[normalizeProducts] ⚠️ is_favorite потерялся для товара:', product.id);
                }
                return normalizedSingle;
              }
            })
          : [],
      };
    });
  };

  const addToFavorites = async (id: number, userId: number, institutionId: number, type: 'single' | 'group' | 'combo') => {
    try {
      await apiFavorite.addToFavorites({ id, institutionId, type });
      // ✅ Проверяем наличие branchId перед обновлением
      if (selectedBranch.value?.id) {
        await fetchProducts(selectedBranch.value.id, userId);
      }
      await fetchFavorites(institutionId);
    } catch (error) {
      consola.error('Ошибка при добавлении товара в избранное:', error);
      throw error;
    }
  };

  const removeFromFavorites = async (id: number, userId: number, institutionId: number, type: 'single' | 'group' | 'combo') => {
    try {
      await apiFavorite.removeFromFavorites({ id, institutionId, type });
      // ✅ Проверяем наличие branchId перед обновлением
      if (selectedBranch.value?.id) {
        await fetchProducts(selectedBranch.value.id, userId);
      }
      await fetchFavorites(institutionId);
    } catch (error) {
      consola.error('Ошибка при удалении товара из избранного:', error);
      throw error;
    }
  };

  // Инициализация вотчеров
  setupWatchers();

  return {
    // Состояние
    institution,
    branches,
    products,
    productsBranchId,
    searchQuery,
    selectedCity,
    selectedBranchId,
    favorites,
    addons,
    excludeIngredients,
    productTags,
    selectedTagIds,

    // Геттеры
    cities,
    branchOptions,
    currentCity,
    selectedBranch,
    favoriteProducts,
    currentId: computed(() => institutionId.value),
    getTagById,
    getTagsByIds,
    hasActiveTagFilters,
    totalProductsCount,
    hasFilteredResults,

    // Действия
    fetchBranches,
    fetchProducts,
    fetchFavorites,
    fetchProductTags,
    addToFavorites,
    removeFromFavorites,
    setInstitutionId,
    setSelectedBranchId,
    chooseBranch,
  };
});
